import React, { useMemo, useState } from 'react'
import Swal from 'sweetalert2'

import IPButton from '../../../../../components/IPButton'
import Table from '../../../../../components/Table'
import IPSelectBox from '../../../../../components/IPSelectBox'
import IPInput from '../../../../../components/IPInput'
import InputCampaign from '../../../../../components/InputCampaign'

import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import { getDateByTimezoneOffset } from '../../../../../base/utils/dataHelper'

import {
  DealerRiskDealerGroupDto,
  DealerRiskReportApiResponseDto,
  DealerRiskReportDto,
  DealerRiskReportRequest,
  DealerRiskReportResponseDto,
} from '../models/models'

import { GetDataApi } from '../services/getDataApi'

interface DealerRiskState {
  DealerCode?: string
  CompanyCode?: string
  MinRiskScore?: string
  MaxRiskScore?: string
  RiskLevel?: string
}

interface GeneralRiskTableRow {
  Order: number
  ReportScope: string
  CompanyCode: string
  RiskLevel: string
  RiskScore: string
  LoyaltyScore: string
  TotalOrderCount: string
  TotalOrderAmount: string
  TotalPaymentAmount: string
  TotalReturnAmount: string
  B2BReturnAmount: string
  NonB2BReturnAmount: string
  B2BReturnToOrderAmountRate: string
  B2BReturnToOrderCountRate: string
  NonB2BReturnAmountShare: string
  DaysSinceLastActivity: string
  MlSegment: string
}

interface RiskSummaryTableRow extends GeneralRiskTableRow {
  DealerCode: string
}

interface DealerGroupTableRow extends RiskSummaryTableRow {
  DealerName: string
  CompanyCodes: string
  Detail: React.ReactNode
}

interface CompanyDetailTableRow {
  Order: number
  CompanyCode: string
  RiskLevel: string
  RiskScore: string
  LoyaltyScore: string
  FrequencyScore: string

  TotalOrderCount: string
  TotalOrderAmount: string
  TotalOrderQuantity: string
  AverageOrderAmount: string

  TotalPaymentCount: string
  TotalPaymentAmount: string

  TotalReturnCount: string
  TotalReturnAmount: string
  TotalReturnQuantity: string

  B2BReturnCount: string
  B2BReturnAmount: string
  B2BReturnQuantity: string

  NonB2BReturnCount: string
  NonB2BReturnAmount: string
  NonB2BReturnQuantity: string

  B2BReturnAmountShare: string
  NonB2BReturnAmountShare: string
  B2BReturnToOrderAmountRate: string
  B2BReturnToOrderCountRate: string

  MonthlyOrderFrequency: string
  ActiveDayCount: string
  ActiveMonthCount: string
  DaysSinceLastActivity: string

  CampaignOrderCount: string
  CampaignUsageRate: string

  MlClusterId: string
  MlSegment: string
  MlOutlierScore: string

  RiskReasons: React.ReactNode
  Recommendation: string
}

function Index(props: BaseComponentWithContextProps) {
  const [state, setState] = useState<DealerRiskState>({})
  const [dealerRiskResponse, setDealerRiskResponse] = useState<DealerRiskReportResponseDto | undefined>()
  const [selectedDealer, setSelectedDealer] = useState<DealerRiskDealerGroupDto | undefined>()

  const initialStartDate = new Date(2023, 7, 1)

  const [dealerRiskStartDate, setDealerRiskStartDate] = useState<Date>(initialStartDate)
  const [dealerRiskEndDate, setDealerRiskEndDate] = useState<Date>(new Date())

  const tableScrollStyle: React.CSSProperties = {
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: 8,
  }

  const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 1050,
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  }

  const modalStyle: React.CSSProperties = {
    width: '96%',
    maxWidth: 1500,
    maxHeight: '90vh',
    backgroundColor: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 10px 35px rgba(0, 0, 0, 0.25)',
  }

  const modalBodyStyle: React.CSSProperties = {
    padding: 16,
    maxHeight: 'calc(90vh - 72px)',
    overflowY: 'auto',
  }

  const formatDateToInputValue = (date: Date) => {
    return date.toLocaleDateString('sv-SE')
  }

  const toNumber = (value: unknown): number => {
    const numberValue = Number(value)
    return Number.isFinite(numberValue) ? numberValue : 0
  }

  const formatDecimal = (value: unknown, fractionDigits = 2) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(toNumber(value))
  }

  const formatInteger = (value: unknown) => {
    return new Intl.NumberFormat('tr-TR', {
      maximumFractionDigits: 0,
    }).format(toNumber(value))
  }

  const formatMoney = (value: unknown) => {
    return `${formatDecimal(value, 2)} TL`
  }

  const formatPercent = (value: unknown) => {
    return `${formatDecimal(toNumber(value) * 100, 2)} %`
  }

  const formatScore = (value: unknown) => {
    return formatDecimal(value, 2)
  }

  const truncateText = (value?: string, maxLength = 90) => {
    if (!value) {
      return '-'
    }

    if (value.length <= maxLength) {
      return value
    }

    return `${value.substring(0, maxLength)}...`
  }

  const renderMultilineText = (value?: string) => {
    if (!value) {
      return '-'
    }

    const items = value.split('|')

    return (
      <div style={{ minWidth: 260, whiteSpace: 'normal', lineHeight: 1.6 }}>
        {items.map((item, index) => (
          <React.Fragment key={`${item}-${index}`}>
            {item.trim()}
            {index < items.length - 1 && (
              <>
                <br />
                <br />
              </>
            )}
          </React.Fragment>
        ))}
      </div>
    )
  }

  const emptyRiskDto = (): DealerRiskReportDto => ({
    DealerCode: '',
    CompanyCode: '',
    ReportScope: '',
    IsGroupSummary: false,

    TotalOrderCount: 0,
    TotalOrderAmount: 0,
    TotalOrderQuantity: 0,
    AverageOrderAmount: 0,

    CampaignOrderCount: 0,
    CampaignUsageRate: 0,

    TotalPaymentCount: 0,
    TotalPaymentAmount: 0,

    TotalReturnCount: 0,
    TotalReturnAmount: 0,
    TotalReturnQuantity: 0,

    B2BReturnCount: 0,
    B2BReturnAmount: 0,
    B2BReturnQuantity: 0,

    NonB2BReturnCount: 0,
    NonB2BReturnAmount: 0,
    NonB2BReturnQuantity: 0,

    B2BReturnAmountShare: 0,
    NonB2BReturnAmountShare: 0,

    B2BReturnToOrderAmountRate: 0,
    B2BReturnToOrderCountRate: 0,

    ActiveDayCount: 0,
    ActiveMonthCount: 0,
    MonthlyOrderFrequency: 0,

    FirstActionDate: null,
    LastActionDate: null,
    DaysSinceLastActivity: 0,

    FrequencyScore: 0,
    LoyaltyScore: 0,
    RiskScore: 0,
    RiskLevel: '',

    MlClusterId: 0,
    MlSegment: '',
    MlOutlierScore: 0,

    RiskReasons: [],
    Recommendation: '',
    CompanyCodes: [],
  })

  const getRiskResponseData = (
    apiResult: DealerRiskReportApiResponseDto | DealerRiskReportResponseDto | any
  ): DealerRiskReportResponseDto => {
    const data = apiResult?.Data ?? apiResult?.data ?? apiResult

    return {
      Dealers: data?.Dealers ?? [],
      CompanyRisks: data?.CompanyRisks ?? [],
      GeneralGroupRisk: data?.GeneralGroupRisk ?? emptyRiskDto(),
    }
  }

  const parseOptionalNumber = (value?: string) => {
    if (!value || value.trim() === '') {
      return undefined
    }

    const numberValue = Number(value)
    return Number.isFinite(numberValue) ? numberValue : undefined
  }

  const buildGeneralRiskRow = (
    risk: DealerRiskReportDto,
    order: number
  ): GeneralRiskTableRow => {
    return {
      Order: order,
      ReportScope: risk?.ReportScope || '-',
      CompanyCode: risk?.CompanyCode || '-',
      RiskLevel: risk?.RiskLevel || '-',
      RiskScore: formatScore(risk?.RiskScore),
      LoyaltyScore: formatScore(risk?.LoyaltyScore),
      TotalOrderCount: formatInteger(risk?.TotalOrderCount),
      TotalOrderAmount: formatMoney(risk?.TotalOrderAmount),
      TotalPaymentAmount: formatMoney(risk?.TotalPaymentAmount),
      TotalReturnAmount: formatMoney(risk?.TotalReturnAmount),
      B2BReturnAmount: formatMoney(risk?.B2BReturnAmount),
      NonB2BReturnAmount: formatMoney(risk?.NonB2BReturnAmount),
      B2BReturnToOrderAmountRate: formatPercent(risk?.B2BReturnToOrderAmountRate),
      B2BReturnToOrderCountRate: formatPercent(risk?.B2BReturnToOrderCountRate),
      NonB2BReturnAmountShare: formatPercent(risk?.NonB2BReturnAmountShare),
      DaysSinceLastActivity: formatInteger(risk?.DaysSinceLastActivity),
      MlSegment: (risk?.MlSegment),
    }
  }

  const buildRiskSummaryRow = (
    risk: DealerRiskReportDto,
    order: number,
    dealerCodeOverride?: string
  ): RiskSummaryTableRow => {
    return {
      DealerCode: dealerCodeOverride || risk?.DealerCode || '-',
      ...buildGeneralRiskRow(risk, order),
    }
  }

  const buildDealerGroupRow = (
    dealer: DealerRiskDealerGroupDto,
    order: number
  ): DealerGroupTableRow => {
    const groupRisk = dealer.GroupRisk

    return {
      ...buildRiskSummaryRow(groupRisk, order, dealer.DealerCode),
      DealerName: dealer.DealerName || '-',
      CompanyCodes: groupRisk?.CompanyCodes?.join(', ') || '-',
      Detail: (
        <IPButton
          type="button"
          className="btn btn-sm btn-primary"
          text="Detay"
          disabled={!dealer.CompanyRisks || dealer.CompanyRisks.length === 0}
          onClick={() => setSelectedDealer(dealer)}
        />
      ),
    }
  }

  const buildCompanyDetailRow = (
    risk: DealerRiskReportDto,
    order: number
  ): CompanyDetailTableRow => {
    const riskReasons = risk?.RiskReasons?.join(' | ') || ''
    const recommendation = risk?.Recommendation || ''

    return {
      Order: order,
      CompanyCode: risk?.CompanyCode || '-',
      RiskLevel: risk?.RiskLevel || '-',
      RiskScore: formatScore(risk?.RiskScore),
      LoyaltyScore: formatScore(risk?.LoyaltyScore),
      FrequencyScore: formatScore(risk?.FrequencyScore),

      TotalOrderCount: formatInteger(risk?.TotalOrderCount),
      TotalOrderAmount: formatMoney(risk?.TotalOrderAmount),
      TotalOrderQuantity: formatDecimal(risk?.TotalOrderQuantity, 2),
      AverageOrderAmount: formatMoney(risk?.AverageOrderAmount),

      TotalPaymentCount: formatInteger(risk?.TotalPaymentCount),
      TotalPaymentAmount: formatMoney(risk?.TotalPaymentAmount),

      TotalReturnCount: formatInteger(risk?.TotalReturnCount),
      TotalReturnAmount: formatMoney(risk?.TotalReturnAmount),
      TotalReturnQuantity: formatDecimal(risk?.TotalReturnQuantity, 2),

      B2BReturnCount: formatInteger(risk?.B2BReturnCount),
      B2BReturnAmount: formatMoney(risk?.B2BReturnAmount),
      B2BReturnQuantity: formatDecimal(risk?.B2BReturnQuantity, 2),

      NonB2BReturnCount: formatInteger(risk?.NonB2BReturnCount),
      NonB2BReturnAmount: formatMoney(risk?.NonB2BReturnAmount),
      NonB2BReturnQuantity: formatDecimal(risk?.NonB2BReturnQuantity, 2),

      B2BReturnAmountShare: formatPercent(risk?.B2BReturnAmountShare),
      NonB2BReturnAmountShare: formatPercent(risk?.NonB2BReturnAmountShare),
      B2BReturnToOrderAmountRate: formatPercent(risk?.B2BReturnToOrderAmountRate),
      B2BReturnToOrderCountRate: formatPercent(risk?.B2BReturnToOrderCountRate),

      MonthlyOrderFrequency: formatScore(risk?.MonthlyOrderFrequency),

      ActiveDayCount: formatInteger(risk?.ActiveDayCount),
      ActiveMonthCount: formatInteger(risk?.ActiveMonthCount),
      DaysSinceLastActivity: formatInteger(risk?.DaysSinceLastActivity),

      CampaignOrderCount: formatInteger(risk?.CampaignOrderCount),
      CampaignUsageRate: formatPercent(risk?.CampaignUsageRate),

      MlClusterId: formatInteger(risk?.MlClusterId),
      MlSegment: truncateText(risk?.MlSegment, 60),
      MlOutlierScore: formatScore(risk?.MlOutlierScore),

      RiskReasons: renderMultilineText(riskReasons),
      Recommendation: recommendation || '-',
    }
  }

  const generalGroupRiskTableData = useMemo<GeneralRiskTableRow[]>(() => {
    if (!dealerRiskResponse?.GeneralGroupRisk?.ReportScope) {
      return []
    }

    return [
      buildGeneralRiskRow(
        dealerRiskResponse.GeneralGroupRisk,
        1
      ),
    ]
  }, [dealerRiskResponse])

  const companyRiskTableData = useMemo<GeneralRiskTableRow[]>(() => {
    if (!dealerRiskResponse?.CompanyRisks?.length) {
      return []
    }

    return dealerRiskResponse.CompanyRisks.map((risk, index) =>
      buildGeneralRiskRow(risk, index + 1)
    )
  }, [dealerRiskResponse])

  const dealerGroupRiskTableData = useMemo<DealerGroupTableRow[]>(() => {
    if (!dealerRiskResponse?.Dealers?.length) {
      return []
    }

    return dealerRiskResponse.Dealers.map((dealer, index) =>
      buildDealerGroupRow(dealer, index + 1)
    )
  }, [dealerRiskResponse])

  const selectedDealerGroupRiskTableData = useMemo<RiskSummaryTableRow[]>(() => {
    if (!selectedDealer?.GroupRisk) {
      return []
    }

    return [
      buildRiskSummaryRow(
        selectedDealer.GroupRisk,
        1,
        selectedDealer.DealerCode
      ),
    ]
  }, [selectedDealer])

  const selectedDealerCompanyRiskTableData = useMemo<CompanyDetailTableRow[]>(() => {
    if (!selectedDealer?.CompanyRisks?.length) {
      return []
    }

    return selectedDealer.CompanyRisks.map((risk, index) =>
      buildCompanyDetailRow(risk, index + 1)
    )
  }, [selectedDealer])

  const getDealerRiskReport = async () => {
    const req: DealerRiskReportRequest = {
      FromDate: getDateByTimezoneOffset(dealerRiskStartDate) ?? undefined,
      ToDate: getDateByTimezoneOffset(dealerRiskEndDate) ?? undefined,
      UseMlNetSegmentation: true,
      DealerCode: state.DealerCode || undefined,
      CompanyCode: state.CompanyCode || undefined,
      MinRiskScore: parseOptionalNumber(state.MinRiskScore),
      MaxRiskScore: parseOptionalNumber(state.MaxRiskScore),
      RiskLevel: state.RiskLevel || undefined,
    }

    props.showLoading()

    const api = new GetDataApi({})

    try {
      const apiResult = await api.getDealerRiskReport(req)
      const result = getRiskResponseData(apiResult)

      setDealerRiskResponse(result)
      setSelectedDealer(undefined)
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: err?.description || err?.Description || 'Hata oluştu',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    } finally {
      props.hideLoading()
    }
  }

  const generalColumns = [
    { Header: '#', accessor: 'Order' },
    { Header: 'Rapor Kapsamı', accessor: 'ReportScope' },
    { Header: 'Şirket Kodu', accessor: 'CompanyCode' },
    { Header: 'Risk Seviyesi', accessor: 'RiskLevel' },
    { Header: 'Risk Skoru', accessor: 'RiskScore' },
    { Header: 'Sadakat Skoru', accessor: 'LoyaltyScore' },
    { Header: 'Sipariş Adedi', accessor: 'TotalOrderCount' },
    { Header: 'Sipariş Tutarı', accessor: 'TotalOrderAmount' },
    { Header: 'Ödeme Tutarı', accessor: 'TotalPaymentAmount' },
    { Header: 'Toplam İade Tutarı', accessor: 'TotalReturnAmount' },
    { Header: 'B2B İade Tutarı', accessor: 'B2BReturnAmount' },
    { Header: 'B2B Dışı İade Tutarı', accessor: 'NonB2BReturnAmount' },
    { Header: 'B2B İade / Sipariş Tutarı', accessor: 'B2BReturnToOrderAmountRate' },
    { Header: 'B2B İade / Sipariş Adedi', accessor: 'B2BReturnToOrderCountRate' },
    { Header: 'B2B Dışı İade Payı', accessor: 'NonB2BReturnAmountShare' },
    { Header: 'Son Aktivite Gün', accessor: 'DaysSinceLastActivity' },
    { Header: 'ML Segment', accessor: 'MlSegment' },
  ]

  const summaryColumns = [
    { Header: '#', accessor: 'Order' },
    { Header: 'Rapor Kapsamı', accessor: 'ReportScope' },
    { Header: 'Bayi Kodu', accessor: 'DealerCode' },
    { Header: 'Şirket Kodu', accessor: 'CompanyCode' },
    { Header: 'Risk Seviyesi', accessor: 'RiskLevel' },
    { Header: 'Risk Skoru', accessor: 'RiskScore' },
    { Header: 'Sadakat Skoru', accessor: 'LoyaltyScore' },
    { Header: 'Sipariş Adedi', accessor: 'TotalOrderCount' },
    { Header: 'Sipariş Tutarı', accessor: 'TotalOrderAmount' },
    { Header: 'Ödeme Tutarı', accessor: 'TotalPaymentAmount' },
    { Header: 'Toplam İade Tutarı', accessor: 'TotalReturnAmount' },
    { Header: 'B2B İade Tutarı', accessor: 'B2BReturnAmount' },
    { Header: 'B2B Dışı İade Tutarı', accessor: 'NonB2BReturnAmount' },
    { Header: 'B2B İade / Sipariş Tutarı', accessor: 'B2BReturnToOrderAmountRate' },
    { Header: 'B2B İade / Sipariş Adedi', accessor: 'B2BReturnToOrderCountRate' },
    { Header: 'B2B Dışı İade Payı', accessor: 'NonB2BReturnAmountShare' },
    { Header: 'Son Aktivite Gün', accessor: 'DaysSinceLastActivity' },
    { Header: 'ML Segment', accessor: 'MlSegment' },
  ]

  const dealerGroupColumns = [
    { Header: '#', accessor: 'Order' },
    { Header: 'Bayi Kodu', accessor: 'DealerCode' },
    { Header: 'Bayi Adı', accessor: 'DealerName' },
    { Header: 'Şirketler', accessor: 'CompanyCodes' },
    { Header: 'Rapor Kapsamı', accessor: 'ReportScope' },
    { Header: 'Risk Seviyesi', accessor: 'RiskLevel' },
    { Header: 'Risk Skoru', accessor: 'RiskScore' },
    { Header: 'Sadakat Skoru', accessor: 'LoyaltyScore' },
    { Header: 'Sipariş Adedi', accessor: 'TotalOrderCount' },
    { Header: 'Sipariş Tutarı', accessor: 'TotalOrderAmount' },
    { Header: 'Ödeme Tutarı', accessor: 'TotalPaymentAmount' },
    { Header: 'Toplam İade Tutarı', accessor: 'TotalReturnAmount' },
    { Header: 'B2B İade Tutarı', accessor: 'B2BReturnAmount' },
    { Header: 'B2B Dışı İade Tutarı', accessor: 'NonB2BReturnAmount' },
    { Header: 'B2B İade / Sipariş Tutarı', accessor: 'B2BReturnToOrderAmountRate' },
    { Header: 'B2B İade / Sipariş Adedi', accessor: 'B2BReturnToOrderCountRate' },
    { Header: 'B2B Dışı İade Payı', accessor: 'NonB2BReturnAmountShare' },
    { Header: 'Son Aktivite Gün', accessor: 'DaysSinceLastActivity' },
    { Header: 'ML Segment', accessor: 'MlSegment' },
    { Header: 'Detay', accessor: 'Detail' },
  ]

  const companyDetailColumns = [
    { Header: '#', accessor: 'Order' },
    { Header: 'Şirket Kodu', accessor: 'CompanyCode' },
    { Header: 'Risk Seviyesi', accessor: 'RiskLevel' },
    { Header: 'Risk Skoru', accessor: 'RiskScore' },
    { Header: 'Sadakat Skoru', accessor: 'LoyaltyScore' },
    { Header: 'Frekans Skoru', accessor: 'FrequencyScore' },

    { Header: 'Sipariş Adedi', accessor: 'TotalOrderCount' },
    { Header: 'Sipariş Tutarı', accessor: 'TotalOrderAmount' },
    { Header: 'Sipariş Miktarı', accessor: 'TotalOrderQuantity' },
    { Header: 'Ortalama Sipariş', accessor: 'AverageOrderAmount' },

    { Header: 'Ödeme Adedi', accessor: 'TotalPaymentCount' },
    { Header: 'Ödeme Tutarı', accessor: 'TotalPaymentAmount' },

    { Header: 'Toplam İade Adedi', accessor: 'TotalReturnCount' },
    { Header: 'Toplam İade Tutarı', accessor: 'TotalReturnAmount' },
    { Header: 'Toplam İade Miktarı', accessor: 'TotalReturnQuantity' },

    { Header: 'B2B İade Adedi', accessor: 'B2BReturnCount' },
    { Header: 'B2B İade Tutarı', accessor: 'B2BReturnAmount' },
    { Header: 'B2B İade Miktarı', accessor: 'B2BReturnQuantity' },

    { Header: 'B2B Dışı İade Adedi', accessor: 'NonB2BReturnCount' },
    { Header: 'B2B Dışı İade Tutarı', accessor: 'NonB2BReturnAmount' },
    { Header: 'B2B Dışı İade Miktarı', accessor: 'NonB2BReturnQuantity' },

    { Header: 'B2B İade Payı', accessor: 'B2BReturnAmountShare' },
    { Header: 'B2B Dışı İade Payı', accessor: 'NonB2BReturnAmountShare' },
    { Header: 'B2B İade / Sipariş Tutarı', accessor: 'B2BReturnToOrderAmountRate' },
    { Header: 'B2B İade / Sipariş Adedi', accessor: 'B2BReturnToOrderCountRate' },

    { Header: 'Aylık Sipariş Frekansı', accessor: 'MonthlyOrderFrequency' },
    { Header: 'Aktif Gün', accessor: 'ActiveDayCount' },
    { Header: 'Aktif Ay', accessor: 'ActiveMonthCount' },
    { Header: 'Son Aktivite Gün', accessor: 'DaysSinceLastActivity' },

    { Header: 'Kampanya Sipariş', accessor: 'CampaignOrderCount' },
    { Header: 'Kampanya Kullanım', accessor: 'CampaignUsageRate' },

    { Header: 'ML Cluster', accessor: 'MlClusterId' },
    { Header: 'ML Segment', accessor: 'MlSegment' },
    { Header: 'ML Outlier', accessor: 'MlOutlierScore' },

    { Header: 'Risk Nedenleri', accessor: 'RiskReasons' },
    { Header: 'Tavsiye', accessor: 'Recommendation' },
  ]

  return (
    <div className="card">
      <div className="card-header">
        <span className="h4">Bayi Risk Raporu</span>
      </div>

      <div className="card-body">
        <form className="formbanner">
          <div className="row">
            <div className="form-group col-md-2">
              <InputCampaign
                type="date"
                id="RiskStartDate"
                name="RiskStartDate"
                label="Başlangıç Tarihi"
                value={formatDateToInputValue(dealerRiskStartDate)}
                onChange={e => setDealerRiskStartDate(new Date((e as any).target.value))}
                isActive
                disabled
              />
            </div>

            <div className="form-group col-md-2">
              <InputCampaign
                type="date"
                id="RiskEndDate"
                name="RiskEndDate"
                label="Bitiş Tarihi"
                value={formatDateToInputValue(dealerRiskEndDate)}
                onChange={e => setDealerRiskEndDate(new Date((e as any).target.value))}
                isActive
                disabled
              />
            </div>

            <div className="form-group col-md-2">
              <IPInput
                type="text"
                id="RiskDealerCode"
                className="form-control"
                name="RiskDealerCode"
                placeholder="Bayi Kodu"
                onChange={e => setState({ ...state, DealerCode: (e as any).target.value })}
                value={state.DealerCode || ''}
              />
            </div>

            <div className="form-group col-md-2">
              <IPInput
                type="text"
                id="RiskCompanyCode"
                className="form-control"
                name="RiskCompanyCode"
                placeholder="Şirket Kodu"
                onChange={e => setState({ ...state, CompanyCode: (e as any).target.value })}
                value={state.CompanyCode || ''}
              />
            </div>

            <div className="form-group col-md-2">
              <IPSelectBox
                isClearable
                id="RiskLevel"
                name="RiskLevel"
                placeholder="Risk Seviyesi"
                options={[
                  { label: 'Düşük', value: 'Düşük' },
                  { label: 'Orta', value: 'Orta' },
                  { label: 'Yüksek', value: 'Yüksek' },
                  { label: 'Kritik', value: 'Kritik' },
                ]}
                value={state.RiskLevel ? { label: state.RiskLevel, value: state.RiskLevel } : undefined}
                onChangeSingle={option =>
                  setState({
                    ...state,
                    RiskLevel: option?.value ? String(option.value) : undefined,
                  })
                }
              />
            </div>

            <div className="col-md-2 mb-3">
              <IPButton
                type="button"
                className="btn btn-success"
                text="Filtrele"
                onClick={getDealerRiskReport}
              />
            </div>
          </div>
        </form>

        {dealerRiskResponse && (
          <div className="mb-3">
            <strong>Özet: </strong>
            {dealerRiskResponse.Dealers?.length || 0} bayi,{' '}
            {dealerRiskResponse.CompanyRisks?.length || 0} şirket geneli risk satırı listeleniyor.
          </div>
        )}

        <div className="mt-4" style={tableScrollStyle}>
          <Table
            columns={generalColumns}
            data={generalGroupRiskTableData}
            currentPage={0}
            headerName="Genel Grup Riski"
            columnCount={6}
          />
        </div>

        <div className="mt-4" style={tableScrollStyle}>
          <Table
            columns={generalColumns}
            data={companyRiskTableData}
            currentPage={0}
            headerName="Şirket Geneli Riskleri"
            columnCount={6}
          />
        </div>

        <div className="mt-4" style={tableScrollStyle}>
          <Table
            columns={dealerGroupColumns}
            data={dealerGroupRiskTableData}
            currentPage={0}
            headerName="Bayi Bazlı Grup Riskleri"
            columnCount={6}
          />
        </div>
      </div>

      {selectedDealer && (
        <div style={modalOverlayStyle}>
          <div style={modalStyle}>
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <div>
                <h5 className="mb-0">Bayi Şirket Risk Detayları</h5>
                <small>
                  Bayi Kodu: <strong>{selectedDealer.DealerCode}</strong>
                </small>
              </div>

              <IPButton
                type="button"
                className="btn btn-sm btn-outline-secondary"
                text="Kapat"
                onClick={() => setSelectedDealer(undefined)}
              />
            </div>

            <div style={modalBodyStyle}>
              <div style={tableScrollStyle}>
                <Table
                  columns={summaryColumns}
                  data={selectedDealerGroupRiskTableData}
                  currentPage={0}
                  headerName="Bayi Grup Özeti"
                  columnCount={6}
                />
              </div>

              <div className="mt-4" style={tableScrollStyle}>
                <Table
                  columns={companyDetailColumns}
                  data={selectedDealerCompanyRiskTableData}
                  currentPage={0}
                  headerName="Şirket Bazlı Riskler"
                  columnCount={6}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default componentWithContext(Index)